import React from 'react'
import TextField from '@mui/material/TextField'
import { Editor } from '@tinymce/tinymce-react'
import DOMPurify from 'dompurify'
import BaseDisplayItem from './BaseDisplayItem'
import ConfirmRedirectWebpageDialog from '../../Components/Form/ConfirmRedirectWebpageDialog.jsx'

/* ---------- Types ---------- */

interface Translation {
    culture: string
    name: string
}

interface Item {
    id: string | number
    translations?: Translation[]
}

interface Kedo {
    t: (key: string) => string
    env: () => {
        getSelectedLocale: () => { code: string }
    }
    api: () => {
        checkTokenForExpiryAndRenew: () => void
    }
}

interface Props {
    item: Item
    value: string
    mode: string
    errors?: string | string[]
    custom_layout?: string
    kedo: Kedo
    changeValue?: (id: Item['id'], value: string) => void
    onKeyPress?: (item: Item, event: React.KeyboardEvent) => void
}

interface State {
    value: string
    readMore: boolean
    lastChanged: number | null
    confirmRedirect: string | null
}

/* ---------- Component ---------- */

class TextareaField extends BaseDisplayItem<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            ...this.state,
            readMore: false,
            lastChanged: null,
            confirmRedirect: null,
        }

        this.onChangeRich = this.onChangeRich.bind(this)
        this.handleLinkClick = this.handleLinkClick.bind(this)
    }

    /* ---------- Helpers ---------- */

    getTranslationFromItem(item: Item): Translation | undefined {
        if (!item.translations?.length) return

        const locale = this.props.kedo.env().getSelectedLocale()

        return (
            item.translations.find((t) => t.culture === locale.code) ||
            item.translations[0]
        )
    }

    /* ---------- Change handlers ---------- */

    onChangeRich(content: string) {
        let { lastChanged } = this.state

        if (lastChanged && Date.now() - lastChanged > 30_000) {
            this.props.kedo.api().checkTokenForExpiryAndRenew()
            lastChanged = Date.now()
        } else if (!lastChanged) {
            lastChanged = Date.now()
        }

        this.setState({ value: content, lastChanged })
        this.props.changeValue?.(this.props.item.id, content)
    }

    /* ---------- Render: EDIT ---------- */

    renderEdit(): React.ReactNode {
        if (this.props.custom_layout === 'row_input') {
            const trans = this.getTranslationFromItem(this.props.item)

            return (
                <TextField
                    size="small"
                    fullWidth
                    inputRef={this.innerRef}
                    value={this.state.value}
                    placeholder={trans?.name}
                    error={this.isInvalid()}
                    helperText={this.isInvalid() ? this.props.errors : ''}
                    onChange={(e) => this.updateValue(e.target.value)}
                    onKeyPress={
                        this.props.onKeyPress
                            ? (e) => this.props.onKeyPress(this.props.item, e)
                            : undefined
                    }
                    inputProps={{ maxLength: 255 }}
                />
            )
        }

        return (
            <div ref={this.innerRef}>
                <div className={this.isInvalid() ? 'invalidMCE' : ''}>
                    <Editor
                        initialValue={this.isInvalid() ? '' : this.props.value}
                        onEditorChange={this.onChangeRich}
                        init={{
                            branding: false,
                            default_link_target: '_blank',
                            relative_urls: false,
                            remove_script_host: true,
                            convert_urls: true,
                            height: 250,
                            body_class: 'tinyMCE-editor',
                            browser_spellcheck: true,
                            contextmenu: false,
                            plugins: [
                                'table',
                                'lists',
                                'link',
                                'autolink',
                                'emoticons',
                                'help',
                            ],
                            menubar: false,
                            toolbar:
                                'undo redo | styleselect | fontselect fontsizeselect bold italic underline strikethrough forecolor backcolor | hr link | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | table | emoticons | removeformat | paste | help',
                            toolbar_mode: 'sliding',
                            paste_data_images: true,
                        }}
                    />
                </div>

                {this.isInvalid() && (
                    <span className="fake-feedback">{this.props.errors}</span>
                )}
            </div>
        )
    }

    /* ---------- Render: SHOW ---------- */

    handleLinkClick(e: MouseEvent) {
        const target = e.target as HTMLAnchorElement

        if (target?.tagName === 'A' && target.href) {
            e.preventDefault()
            this.setState({
                confirmRedirect: target.href,
            })
        }
    }

    renderShow(): React.ReactNode {
        const maxLength = this.props.mode === 'list' ? 160 : 450

        if (!this.props.value?.slice) return null

        const allowedRules =
            this.props.mode === 'list'
                ? {
                      ALLOWED_TAGS: [
                          'b',
                          'br',
                          'span',
                          'table',
                          'ol',
                          'tbody',
                          'li',
                          'i',
                          'strong',
                          'th',
                          'tr',
                          'td',
                          'thead',
                          'div',
                          'p',
                          'h1',
                          'h2',
                          'img',
                          'a',
                      ],
                      ALLOWED_ATTR: ['href'],
                  }
                : {
                      ALLOWED_TAGS: [
                          'b',
                          'br',
                          'span',
                          'table',
                          'ol',
                          'tbody',
                          'li',
                          'i',
                          'strong',
                          'th',
                          'tr',
                          'td',
                          'thead',
                          'div',
                          'p',
                          's',
                          'h1',
                          'h2',
                          'img',
                          'a',
                      ],
                      ALLOWED_ATTR: ['style', 'border', 'href'],
                  }

        const sanitizedHTML = (html: string) =>
            DOMPurify.sanitize(html, allowedRules)

        const truncatedValue =
            this.props.value.length > maxLength
                ? `${this.props.value.slice(0, maxLength)}...`
                : this.props.value

        return (
            <>
                {this.state.confirmRedirect && (
                    <ConfirmRedirectWebpageDialog
                        kedo={this.props.kedo}
                        accept={() => {
                            window.open(this.state.confirmRedirect!, '_blank')
                            this.setState({
                                confirmRedirect: null,
                            })
                        }}
                        cancel={() =>
                            this.setState({
                                confirmRedirect: null,
                            })
                        }
                    />
                )}

                <div
                    ref={(div) => {
                        if (div) {
                            div.querySelectorAll('a').forEach((link) => {
                                link.addEventListener(
                                    'click',
                                    this.handleLinkClick
                                )
                            })
                        }
                    }}
                    style={{ pointerEvents: 'none' }}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: sanitizedHTML(
                                this.state.readMore
                                    ? this.props.value
                                    : truncatedValue
                            ),
                        }}
                        style={{ pointerEvents: 'auto' }}
                    />

                    {this.props.value.length > maxLength && (
                        <a
                            className="readMore"
                            style={{
                                pointerEvents: 'auto',
                            }}
                            onClick={() =>
                                this.setState({
                                    readMore: !this.state.readMore,
                                })
                            }
                        >
                            {this.state.readMore
                                ? this.props.kedo.t('Read less')
                                : this.props.kedo.t('Read more')}
                        </a>
                    )}
                </div>
            </>
        )
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        return (
            <TextField
                size="small"
                fullWidth
                type="text"
                value={this.props.value}
                onChange={(e) =>
                    this.props.changeValue?.(this.props.item.id, e.target.value)
                }
                onKeyPress={
                    this.props.onKeyPress
                        ? (e) => this.props.onKeyPress(this.props.item, e)
                        : undefined
                }
            />
        )
    }
}

export default TextareaField
